import React, { useEffect, useState, useRef, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";

// Pages
import Landing from "./pages/Landing";
import Dashboard from "./pages/Dashboard";
import CollegeDetail from "./pages/CollegeDetail";
import SubmitPlacement from "./pages/SubmitPlacement";
import Profile from "./pages/Profile";
import Login from "./pages/Login";
import Admin from "./pages/Admin";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsOfService from "./pages/TermsOfService";
import AboutUs from "./pages/AboutUs";

// Components
import { ThemeProvider } from "./components/ThemeProvider";
import BottomNav from "./components/BottomNav";
import BackToTop from "./components/BackToTop";
import FeedbackDialog from "./components/FeedbackDialog";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

// Token helpers
const TOKEN_KEY = 'transplacement_token';
const getToken = () => localStorage.getItem(TOKEN_KEY);
const setToken = (t) => t ? localStorage.setItem(TOKEN_KEY, t) : localStorage.removeItem(TOKEN_KEY);

// Axios interceptor — attach JWT to every request.
// For admin endpoints only, also send cookies (admin session uses HttpOnly cookie).
axios.interceptors.request.use((config) => {
  const token = getToken();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (typeof config.url === 'string' && config.url.includes('/admin/')) {
    config.withCredentials = true;
  }
  return config;
}, (error) => Promise.reject(error));

// ─── Google Sign-In Modal ────────────────────────────────────────────────────
// Renders the native GIS button inside a centered overlay.
// Mounts fresh each time so GIS re-initialises cleanly.

function GoogleSignInModal({ onCredential, onClose }) {
  const btnRef = useRef(null);

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID || !btnRef.current) return;

    const renderBtn = () => {
      if (!btnRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (r) => onCredential(r.credential),
        auto_select: false,
        cancel_on_tap_outside: true,
        use_fedcm_for_prompt: false,
      });
      window.google.accounts.id.renderButton(btnRef.current, {
        type: 'standard',
        theme: 'outline',
        size: 'large',
        text: 'continue_with',
        shape: 'rectangular',
        width: 320,
      });
    };

    if (window.google?.accounts?.id) {
      renderBtn();
    } else {
      // Load GIS script once; reuse if already injected
      let script = document.querySelector('script[src*="accounts.google.com/gsi"]');
      if (!script) {
        script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        document.head.appendChild(script);
      }
      script.addEventListener('load', renderBtn, { once: true });
    }
  }, [onCredential]);

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 8 }}
        transition={{ duration: 0.15 }}
        className="bg-background border border-border rounded-2xl p-8 w-full max-w-sm mx-4 shadow-2xl relative"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-1.5 rounded-full hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="text-center mb-6">
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight">Sign In</h2>
          <p className="text-muted-foreground text-sm mt-2">
            Continue with your Google account to submit placements.
          </p>
        </div>

        {/* Native GIS button renders here */}
        <div ref={btnRef} className="flex justify-center min-h-[44px]" style={{ colorScheme: 'auto' }} />

        <p className="text-center text-[10px] text-muted-foreground mt-4">
          Your submissions remain completely anonymous.
        </p>
      </motion.div>
    </div>
  );
}

// ─── Auth Context ────────────────────────────────────────────────────────────

const AuthContext = createContext(null);
const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

// ─── Auth Provider ───────────────────────────────────────────────────────────

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingRedirect, setPendingRedirect] = useState(null);
  const [signInOpen, setSignInOpen] = useState(false);
  const pendingLoginRedirect = useRef('/dashboard');

  // Verify stored JWT on mount (with one retry for transient errors)
  useEffect(() => {
    let mounted = true;
    const init = async () => {
      const token = getToken();
      if (token) {
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const res = await axios.get(`${API}/auth/me`, { timeout: 10000 });
            if (res.data?.user_id && mounted) {
              setUser(res.data);
              localStorage.setItem('transplacement_was_authenticated', 'true');
            }
            break;
          } catch (err) {
            if (err.response?.status === 401) {
              setToken(null);
              if (localStorage.getItem('transplacement_was_authenticated')) {
                toast.info("Your session has expired. Please sign in again to see all rankings.");
              }
              break;
            }
            if (attempt < 1) await new Promise(r => setTimeout(r, 1500));
          }
        }
      }
      if (mounted) setLoading(false);
    };
    init();
    return () => { mounted = false; };
  }, []);

  // Called by GoogleSignInModal when Google returns a credential
  const handleGoogleCredential = useCallback(async (credential) => {
    setSignInOpen(false);
    try {
      const res = await axios.post(`${API}/auth/google`, { google_token: credential });
      if (res.data?.session_token) {
        setToken(res.data.session_token);
        setUser(res.data.user);
        localStorage.setItem('transplacement_was_authenticated', 'true');
        toast.success("Welcome!");
        setPendingRedirect(pendingLoginRedirect.current);
      }
    } catch (err) {
      toast.error(`Sign-in failed: ${err.response?.data?.detail || err.message}`);
    }
  }, []);

  // Opens the sign-in modal directly — no page navigation
  const login = useCallback((redirectTo = '/dashboard') => {
    pendingLoginRedirect.current = redirectTo;
    setSignInOpen(true);
  }, []);

  const logout = useCallback(async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { }
    setToken(null);
    setUser(null);
    localStorage.removeItem('transplacement_was_authenticated');
    toast.success("Logged out");
  }, []);

  return (
    <AuthContext.Provider value={{
      user, loading, login, logout, setUser,
      pendingRedirect, setPendingRedirect,
    }}>
      {children}

      {/* Global sign-in modal — unmounts on close so GIS re-inits cleanly next open */}
      <AnimatePresence>
        {signInOpen && (
          <GoogleSignInModal
            key="signin-modal"
            onCredential={handleGoogleCredential}
            onClose={() => setSignInOpen(false)}
          />
        )}
      </AnimatePresence>
    </AuthContext.Provider>
  );
}

// ─── Redirect Handler ────────────────────────────────────────────────────────

function RedirectHandler() {
  const { pendingRedirect, setPendingRedirect } = useAuth();
  const navigate = useNavigate();
  useEffect(() => {
    if (pendingRedirect) {
      navigate(pendingRedirect, { replace: true });
      setPendingRedirect(null);
    }
  }, [pendingRedirect, navigate, setPendingRedirect]);
  return null;
}

// ─── Loading Spinner ─────────────────────────────────────────────────────────

function LoadingSpinner() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <motion.div className="flex flex-col items-center gap-4" initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        <span className="text-sm text-muted-foreground">Loading...</span>
      </motion.div>
    </div>
  );
}

// ─── Page Transition ─────────────────────────────────────────────────────────

function PageTransition({ children }) {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={location.pathname}
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -8 }}
        transition={{ duration: 0.2 }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

// ─── Feedback Watcher ────────────────────────────────────────────────────────
// Polls /feedback/pending whenever the signed-in user navigates, so a newly
// admin-verified placement triggers the feedback dialog on the very next
// page view — no need to visit Profile.

function FeedbackWatcher() {
  const { user } = useAuth();
  const location = useLocation();
  const [target, setTarget] = useState(null);
  const dismissedRef = useRef(new Set());

  const check = useCallback(async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/feedback/pending`);
      const first = Array.isArray(res.data)
        ? res.data.find((p) => !dismissedRef.current.has(p.placement_id))
        : null;
      if (first) setTarget(first);
    } catch (error) {
      // Silent — non-blocking flow
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      setTarget(null);
      return;
    }
    // Don't spam admin-side routes
    if (location.pathname.startsWith("/admin")) return;
    check();
  }, [user, location.pathname, check]);

  const handleOpenChange = (next) => {
    if (!next && target) {
      // Remember dismissal for this session so it doesn't re-open on next nav
      dismissedRef.current.add(target.placement_id);
      setTarget(null);
    }
  };

  return (
    <FeedbackDialog
      open={!!target}
      onOpenChange={handleOpenChange}
      placement={target}
      onSubmitted={check}
    />
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────

function AppContent() {
  const { loading } = useAuth();
  if (loading) return <LoadingSpinner />;

  return (
    <>
      <RedirectHandler />
      <PageTransition>
        <div className="pb-20 md:pb-0">
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/college/:collegeId" element={<CollegeDetail />} />
            <Route path="/submit" element={<SubmitPlacement />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/about" element={<AboutUs />} />
          </Routes>
        </div>
      </PageTransition>
      <BottomNav />
      <BackToTop />
      <FeedbackWatcher />
      <Toaster position="top-center" richColors closeButton />
    </>
  );
}

function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="transplacement-theme">
      <BrowserRouter>
        <AuthProvider>
          <AppContent />
        </AuthProvider>
      </BrowserRouter>
    </ThemeProvider>
  );
}

export default App;
export { API, useAuth };
